import React from 'react';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const LoadingSpinner = ({ 
  size = 'medium', 
  message, 
  showMessage = true, 
  center = false, 
  overlay = false,
  className = '',
  color = 'primary',
  ariaLabel
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 60
  };
  
  const spinnerSize = sizeMap[size] || sizeMap.medium;
  const displayMessage = message || t('common.loading');
  const accessibilityLabel = ariaLabel || displayMessage;
  
  const spinnerComponent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        ...(center && {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: theme.zIndex.modal
        }),
        ...(overlay && {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(2px)',
          zIndex: theme.zIndex.tooltip,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        })
      }}
      className={className}
      role="status"
      aria-live="polite"
    >
      <CircularProgress 
        size={spinnerSize} 
        color={color}
        aria-label={accessibilityLabel}
      />
      {showMessage && (
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{ 
            textAlign: 'center',
            fontSize: size === 'small' ? '0.75rem' : undefined
          }}
        >
          {displayMessage}
        </Typography>
      )}
    </Box>
  );

  if (center || overlay) {
    return spinnerComponent;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
        minHeight: 100
      }}
    >
      {spinnerComponent}
    </Box>
  );
};

export default LoadingSpinner;